import axios from "axios";
import { API_URL } from "../constants";
import type { FileItem } from "features/files/file-item";

export interface SerializedLeaveRequest {
  id: string;
  userId: string;
  employeeId: string;
  leaveType: string;
  duration: "full_day" | "half_day" | "hourly";
  startDate: string;
  endDate: string;
  startTime: string | null;
  endTime: string | null;
  status: string;
  fileId: string | null;
  fileCategory: string | null;
  /** Files entity in original shape (same naming as Files schema); the Pick is what the status pill reads. */
  file?:
    | (Pick<
        FileItem,
        | "isSigned"
        | "annulledAt"
        | "withdrawnAt"
        | "isApproved"
        | "isApprovalRequired"
        | "filesTemplateCustomField"
      > & {
        id: string;
        name?: string | null;
        title?: string | null;
        fileId?: string | null;
        createdAt?: string | null;
        signType?: string | null;
        signersList?: any[];
        [key: string]: unknown;
      })
    | null;
  comment?: string | null;
  rejectionReason?: string | null;
  /**
   * The viewer only gets who / when / type: fileId, fileCategory, file and
   * rejectionReason come as null. Socket leave-created / leave-updated
   * payloads are always in this shape.
   */
  isRedacted?: boolean;
}

export interface LeaveRequestsPagination {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

function normalizeLeaveRequestsResponse(data: {
  leaveRequests?: SerializedLeaveRequest[];
  pagination?: Partial<LeaveRequestsPagination>;
}): {
  leaveRequests: SerializedLeaveRequest[];
  pagination: LeaveRequestsPagination;
} {
  const leaveRequests = data.leaveRequests ?? [];
  const p = data.pagination;
  return {
    leaveRequests,
    pagination: {
      total: typeof p?.total === "number" ? p.total : leaveRequests.length,
      page: typeof p?.page === "number" ? p.page : 1,
      pageSize:
        typeof p?.pageSize === "number"
          ? p.pageSize
          : leaveRequests.length || 1,
      totalPages:
        typeof p?.totalPages === "number"
          ? p.totalPages
          : leaveRequests.length > 0
            ? 1
            : 1,
    },
  };
}

export type FetchLeaveRequestsParams = {
  from: string;
  to: string;
  employee?: string;
  status?: string;
  leaveType?: string;
  page?: number;
  pageSize?: number;
};

/** Company-wide leave list (e.g. leave calendar). Optional `employee` filter for admins. */
export async function fetchLeaveRequests(
  companyId: string,
  params: FetchLeaveRequestsParams
): Promise<{
  leaveRequests: SerializedLeaveRequest[];
  pagination: LeaveRequestsPagination;
}> {
  const { data } = await axios.get(`${API_URL}/${companyId}/leave-requests`, {
    params,
  });
  return normalizeLeaveRequestsResponse(data);
}

/** Current user's leave list for profile; server enforces session user === userId. */
export async function fetchEmployeeLeaveRequests(
  companyId: string,
  userId: string,
  params: Omit<FetchLeaveRequestsParams, "employee">
): Promise<{
  leaveRequests: SerializedLeaveRequest[];
  pagination: LeaveRequestsPagination;
}> {
  const { data } = await axios.get(
    `${API_URL}/${companyId}/users/${userId}/leave-requests`,
    { params }
  );
  return normalizeLeaveRequestsResponse(data);
}

export interface LeaveSummary {
  leave: number;
  remoteWork: number;
}

export async function fetchLeaveSummaries(
  companyId: string,
  params: { from: string; to: string; employee?: string }
): Promise<Record<string, LeaveSummary>> {
  const { data } = await axios.get(`${API_URL}/${companyId}/leave-requests/summary`, {
    params,
  });
  return data.summaries ?? {};
}

export async function fetchLeaveRequestById(
  companyId: string,
  id: string
): Promise<{ leaveRequest: SerializedLeaveRequest }> {
  const { data } = await axios.get(`${API_URL}/${companyId}/leave-requests/${id}`);
  return data;
}
export interface CreateLeaveRequestBody {
  leaveType: string;
  duration: "full_day" | "half_day" | "hourly";
  startDate: string;
  endDate: string;
  businessTripId?: string;
  startTime?: string;
  endTime?: string;
  comment?: string;
  templateId?: string;
  customFields?: Record<string, unknown>;
}

/** A new request always comes with its generated document. */
export type CreatedLeaveRequest = SerializedLeaveRequest & { fileId: string };

export async function createLeaveRequest(
  companyId: string,
  body: CreateLeaveRequestBody
): Promise<{ leaveRequest: CreatedLeaveRequest }> {
  const { data } = await axios.post(
    `${API_URL}/${companyId}/leave-requests`,
    body
  );
  return data;
}


export async function updateLeaveRequest(
  companyId: string,
  id: string,
  body: Partial<CreateLeaveRequestBody>
): Promise<{ leaveRequest: SerializedLeaveRequest }> {
  const { data } = await axios.patch(
    `${API_URL}/${companyId}/leave-requests/${id}`,
    body
  );
  return data;
}

/** Days of approved leave used in one calendar year, per leave type — erp-backend GET /:companyId/cabinet/leave-usage. */
export interface LeaveUsage {
  year: number;
  /** Only types with days used, in the order of FIXED_LEAVE_TYPE_CODES. */
  byType: { leaveType: string; days: number }[];
}

export async function fetchLeaveUsage(companyId: string, year: number): Promise<LeaveUsage> {
  const { data } = await axios.get(`${API_URL}/${companyId}/cabinet/leave-usage`, {
    params: { year },
  });
  return data;
}
